import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { useDispatch } from "react-redux";
import { showToast } from "../../../components/ToastifyNotification";
import {
  GetUsers,
  GetUserById,
  UpdateUser
} from "../../../api/utility/userAPI";
import { permissions } from "../../../data/permissions";

import {
  collectPermissions,
  isAllChecked,
  isSomeChecked
} from "../../../utils/permissionUtils";
import { UpdateUserPermissions } from "../../../api/utility/userPermissionAPI";

const UserPermissions = () => {
  const dispatch = useDispatch();

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  /* ---------------- FETCH USERS ---------------- */
  useEffect(() => {
    const fetchUsers = async () => {
      const res = await GetUsers();
      if (res?.success) setUsers(res.data);
    };
    fetchUsers();
  }, []);

  /* ---------------- USER SELECTION ---------------- */
  const handleUserSelection = async (userId) => {
    setSelectedUserId(userId);
    if (!userId) {
      setSelectedPermissions([]);
      return;
    }

    try {
      dispatch({ type: "LOADER_START" });
      const res = await GetUserById(userId);
      if (res?.success) {
        setSelectedPermissions(res.data.permissions || []);
      }
    } catch {
      showToast("error", "Failed to load permissions");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  /* ---------------- LEAF TOGGLE ---------------- */
  const handleToggle = (permission) => {
    setSelectedPermissions(prev =>
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  /* ---------------- SELECT ALL TOGGLE ---------------- */
  const toggleNode = (node) => {
    const perms = collectPermissions(node);

    setSelectedPermissions(prev => {
      const hasAll = perms.every(p => prev.includes(p));
      return hasAll
        ? prev.filter(p => !perms.includes(p))
        : Array.from(new Set([...prev, ...perms]));
    });
  };

  /* ---------------- SAVE ---------------- */
  const handleSave = async () => {
    if (!selectedUserId) return;

    try {
      dispatch({ type: "LOADER_START" });
      const data = { permissions: selectedPermissions, id: selectedUserId };
      const res = await UpdateUserPermissions(data);

      if (res?.success) {
        showToast("success", "Permissions updated successfully");
      }
    } catch {
      showToast("error", "Failed to save permissions");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  /* ---------------- RECURSIVE TREE ---------------- */
  const RenderNodes = ({ nodes = [], depth = 0 }) => {
    if (!Array.isArray(nodes)) return null;

    const allLeafNodes =
      nodes.length > 0 && nodes.every(n => n.permission);

    return (
      <div
        style={{
          marginLeft: depth * 20,
          display: allLeafNodes ? "flex" : "block",
          flexWrap: allLeafNodes ? "wrap" : "unset",
          gap: allLeafNodes ? "18px" : "unset"
        }}
      >
        {nodes.map((node, index) => {
          const isLeaf = !!node.permission;

          return (
            <div key={index} className={isLeaf ? "" : "mb-2"}>
              {isLeaf ? (
                <label className="d-flex align-items-center">
                  <input
                    type="checkbox"
                    checked={selectedPermissions.includes(node.permission)}
                    onChange={() => handleToggle(node.permission)}
                    className="me-1"
                  />
                  {node.module}
                </label>
              ) : (
                <div className="d-flex align-items-center mt-4">
                  <input
                    type="checkbox"
                    checked={isAllChecked(node, selectedPermissions)}
                    ref={el => {
                      if (el) {
                        el.indeterminate = isSomeChecked(
                          node,
                          selectedPermissions
                        );
                      }
                    }}
                    onChange={() => toggleNode(node)}
                    className="me-2"
                  />
                  <span className="fw-bold text-primary">
                    {node.module}
                  </span>
                </div>
              )}

              {node.children?.length > 0 && (
                <RenderNodes
                  nodes={node.children}
                  depth={depth + 1}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /* ---------------- UI ---------------- */
  return (
    <Fragment>
      <Breadcrumb parent="Utility" title="User Permissions" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>User Permission Matrix</h5>
          </div>

          <div className="card-body">
            <div className="mb-4 col-md-4">
              <label className="form-label fw-bold">
                Select User
              </label>
              <select
                className="form-select"
                value={selectedUserId}
                onChange={e => handleUserSelection(e.target.value)}
              >
                <option value="">-- Select User --</option>
                {users.map(u => (
                  <option key={u._id} value={u._id}>
                    {u.userName}
                  </option>
                ))}
              </select>
            </div>

            {selectedUserId && (
              <div
                className="border rounded p-3"
                style={{
                  maxHeight: "70vh",
                  overflowY: "auto"
                }}
              >
                <RenderNodes nodes={permissions} />
              </div>
            )}
          </div>

          <div className="card-footer">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!selectedUserId}
            >
              Update Permissions
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default UserPermissions;
